// src/components/AdminLocResendBatch.tsx
//
// Admin tool: resend corrected Letters of Claim to LENDERS ONLY (#696).
// Paste claim refs -> Check (dry run, nothing sent) -> review the table ->
// Send to lenders (up to the per-call cap, confirmed). Clients are never
// emailed: the server forces that off.

import React, { useEffect, useMemo, useState } from 'react';
import {
  LocResendLimits,
  LocResendReason,
  LocResendRow,
  checkLocResend,
  getLocResendReasons,
  sendLocResend,
} from '../api/locResendBatch';
import { Button, Card, PageHeader, StatCard } from './ui';

const DEFAULT_LIMITS: LocResendLimits = { maxChecksPerCall: 25, maxSendsPerCall: 20, paceSeconds: 3 };

/** Same forgiving rules as the server: new lines, commas, spaces; any case. */
export const parseClaimRefs = (text: string): string[] => {
  const seen: string[] = [];
  text
    .split(/[\s,;]+/)
    .map((piece) => piece.trim().replace(/^["']+|["']+$/g, '').toUpperCase())
    .filter(Boolean)
    .forEach((ref) => {
      if (!seen.includes(ref)) seen.push(ref);
    });
  return seen;
};

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

const KEEP_UPPER = new Set(['UK', 'FCE', 'BMW', 'VW', 'VWFS', 'RCI', 'JCB', 'GMAC', 'MBFS', 'HSBC', 'TSB', 'BNP', 'PSA', 'LLP', '(I)']);
const KEEP_LOWER = new Set(['PLC']);

/** Lender names arrive in Lender Management form; any still in ALL CAPS
 *  (the claim's credit-report spelling) are shown in title case. */
export const displayLender = (name?: string | null): string => {
  const text = (name || '').trim();
  if (!text || /[a-z]/.test(text) || !/[A-Z]/.test(text)) return text;
  return text
    .split(/\s+/)
    .map((word) => {
      if (KEEP_UPPER.has(word)) return word;
      if (KEEP_LOWER.has(word)) return word.toLowerCase();
      return word
        .toLowerCase()
        .replace(/(^|[-/(&])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
    })
    .join(' ');
};

const PROBLEM_OUTCOMES = new Set(['failed', 'needs-review', 'not-checked']);

const resultText = (row: LocResendRow): string => {
  switch (row.outcome) {
    case 'would-send':
      return 'Ready to send';
    case 'sent':
      return 'Sent';
    case 'skipped':
      return row.skipReason || 'Skipped';
    case 'not-checked':
      return 'Couldn’t check – press Check again';
    case 'failed':
      return row.detail ? `Failed: ${row.detail}` : 'Failed';
    case 'needs-review':
      return row.detail || 'Needs checking';
    default:
      return row.detail || 'Not sent';
  }
};

const resultClass = (row: LocResendRow): string => {
  if (row.outcome === 'sent') return 'text-green-700 font-medium';
  if (row.outcome === 'would-send') return 'text-teal-700';
  if (row.outcome === 'not-checked') return 'text-amber-600 font-medium';
  if (row.outcome === 'failed' || row.outcome === 'needs-review') return 'text-red-700 font-medium';
  return 'text-gray-500';
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const errorText = (err: any, fallback: string): string =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

const AdminLocResendBatch: React.FC = () => {
  const [reasons, setReasons] = useState<LocResendReason[]>([]);
  const [reason, setReason] = useState('');
  const [limits, setLimits] = useState<LocResendLimits>(DEFAULT_LIMITS);
  const [text, setText] = useState('');
  const [rows, setRows] = useState<LocResendRow[]>([]);
  const [busy, setBusy] = useState<'checking' | 'sending' | null>(null);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [sendNote, setSendNote] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    getLocResendReasons()
      .then((data) => {
        setReasons(data.reasons);
        setLimits(data.limits || DEFAULT_LIMITS);
        if (data.reasons.length === 1) setReason(data.reasons[0].reason);
      })
      .catch((err) => setError(errorText(err, 'Could not load the approved reasons')));
  }, []);

  const refs = useMemo(() => parseClaimRefs(text), [text]);
  const selected = reasons.find((r) => r.reason === reason);
  const isNotice = selected?.kind === 'withdrawal';
  const itemWords = (n: number) =>
    isNotice
      ? `${n} cancellation notice${n === 1 ? '' : 's'}`
      : `${n} letter${n === 1 ? '' : 's'} of claim`;
  const ready = rows.filter((row) => row.outcome === 'would-send');
  const sent = rows.filter((row) => row.outcome === 'sent');
  const problems = rows.filter((row) => PROBLEM_OUTCOMES.has(row.outcome));
  const seriousProblems = problems.some((row) => row.outcome !== 'not-checked');
  const skipped = rows.filter((row) => row.outcome === 'skipped' || row.outcome === 'not-attempted');
  const nextBatch = ready.slice(0, limits.maxSendsPerCall);

  const skipCounts = useMemo(() => {
    const counts = new Map<string, number>();
    skipped.forEach((row) => {
      const key = resultText(row);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [skipped]);

  const readyByLender = useMemo(() => {
    const counts = new Map<string, number>();
    ready.forEach((row) => {
      const key = displayLender(row.lenderName) || 'Unknown lender';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [ready]);

  const changeText = (value: string) => {
    setText(value);
    setRows([]);
    setError('');
    setSendNote(null);
  };

  const changeReason = (value: string) => {
    setReason(value);
    setRows([]);
    setError('');
    setSendNote(null);
  };

  const handleCheck = async () => {
    if (!reason || !refs.length) return;
    setBusy('checking');
    setError('');
    setSendNote(null);
    setRows([]);
    const collected: LocResendRow[] = [];
    try {
      const groups = chunk(refs, limits.maxChecksPerCall);
      for (let i = 0; i < groups.length; i += 1) {
        setProgress(`Checking ${Math.min((i + 1) * limits.maxChecksPerCall, refs.length)} of ${refs.length}…`);
        // eslint-disable-next-line no-await-in-loop
        const data = await checkLocResend(reason, groups[i]);
        collected.push(...data.results);
        setRows([...collected]);
      }
    } catch (err: any) {
      setError(errorText(err, 'Check failed'));
    } finally {
      setBusy(null);
      setProgress('');
    }
  };

  const handleSend = async () => {
    if (!nextBatch.length) return;
    const n = nextBatch.length;
    const confirmed = window.confirm(
      `Send ${itemWords(n)} to lenders?\n\nNo emails will go to clients.`,
    );
    if (!confirmed) return;
    setBusy('sending');
    setError('');
    setSendNote(null);
    setProgress(`Sending ${itemWords(n)}, a few seconds apart…`);
    try {
      const data = await sendLocResend(reason, nextBatch.map((row) => row.claimId));
      const byId = new Map(data.results.map((row) => [row.claimId, row]));
      setRows((current) => current.map((row) => byId.get(row.claimId) || row));
      const sentNow = data.results.filter((row) => row.outcome === 'sent').length;
      const problemsNow = data.results.filter((row) => PROBLEM_OUTCOMES.has(row.outcome)).length;
      setSendNote({
        text: `${isNotice ? plural(sentNow, 'cancellation notice') : plural(sentNow, 'letter')} sent to lenders. ${plural(problemsNow, 'problem')}.`,
        ok: problemsNow === 0,
      });
      if (data.stopped) {
        setError('Sending stopped early. Check the claims marked in red before sending any more.');
      }
    } catch (err: any) {
      setError(
        `${errorText(err, 'Send failed')}. Press Check again before sending more: it shows what went.`,
      );
    } finally {
      setBusy(null);
      setProgress('');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Resend letters to lenders"
        description="Sends a Letter of Claim or cancellation notice again to the lender only, to the lender's current address. No emails go to clients."
      />

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      <Card className="mb-4">
        <div className="space-y-3">
          <div>
            <label htmlFor="loc-resend-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <select
              id="loc-resend-reason"
              value={reason}
              onChange={(e) => changeReason(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm max-w-md w-full"
            >
              <option value="">Choose a reason…</option>
              {reasons.map((r) => (
                <option key={r.batchKey} value={r.reason}>
                  {r.reason}
                </option>
              ))}
            </select>
            {selected && (
              <p className="mt-1 text-xs text-gray-500">
                Sends {selected.sends || 'letters of claim'}. {selected.allowedCount} claims are approved for
                this reason. Any other claim is skipped.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="loc-resend-refs" className="block text-sm font-medium text-gray-700 mb-1">
              Claim references
            </label>
            <textarea
              id="loc-resend-refs"
              value={text}
              onChange={(e) => changeText(e.target.value)}
              rows={6}
              placeholder="Paste claim references, one per line"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">{refs.length} claim{refs.length === 1 ? '' : 's'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={handleCheck} disabled={!reason || !refs.length || !!busy}>
              Check
            </Button>
            {progress && <span className="text-sm text-gray-500">{progress}</span>}
          </div>
        </div>
      </Card>

      {rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard value={ready.length} label="Ready to send" />
            <StatCard value={sent.length} label="Sent" tone="success" />
            <StatCard value={skipped.length} label="Skipped" tone="muted" />
            <StatCard
              value={problems.length}
              label="Problems"
              tone={seriousProblems ? 'error' : problems.length ? 'default' : 'muted'}
            />
          </div>

          <Card className="mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="primary" onClick={handleSend} disabled={!nextBatch.length || !!busy}>
                Send to lenders ({nextBatch.length})
              </Button>
              <span className="text-sm text-gray-600">
                Sends up to {limits.maxSendsPerCall} at a time, a few seconds apart. No emails go to clients.
              </span>
            </div>
            {(readyByLender.length > 0 || skipCounts.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                {readyByLender.length > 0 && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Ready, by lender</div>
                    <ul className="text-gray-600">
                      {readyByLender.map(([lender, count]) => (
                        <li key={lender}>
                          {lender}: {count}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {skipCounts.length > 0 && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Skipped because</div>
                    <ul className="text-gray-600">
                      {skipCounts.map(([why, count]) => (
                        <li key={why}>
                          {why}: {count}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>

          {sendNote && (
            <p
              role="status"
              className={`mb-3 text-sm font-medium ${sendNote.ok ? 'text-green-700' : 'text-red-700'}`}
            >
              {sendNote.text}
            </p>
          )}

          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Claim</th>
                    <th className="py-2 pr-4 font-medium">Lender</th>
                    <th className="py-2 pr-4 font-medium">Sends to</th>
                    {!isNotice && <th className="py-2 pr-4 font-medium">Agreement number on letter</th>}
                    <th className="py-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.claimId} className="border-b border-gray-100" data-testid="loc-resend-row">
                      <td className="py-2 pr-4 font-mono">{row.claimId}</td>
                      <td className="py-2 pr-4">{displayLender(row.lenderName) || '—'}</td>
                      <td className="py-2 pr-4">{row.recipient || '—'}</td>
                      {!isNotice && <td className="py-2 pr-4 font-mono">{row.agreementNumber || '—'}</td>}
                      <td className={`py-2 ${resultClass(row)}`}>{resultText(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminLocResendBatch;
